import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import { Calendar } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import {
  addRowToPayload,
  calendarLocalizer,
  convertToFormRowUpdate,
  fetchApi,
  makeCompoundPayload,
  notifyError,
  notifyResponse,
  PageTitle,
} from 'gossamer-front-end';
import {
  ChatInitializationResponse,
  ChatMessageRequest,
  ChatMessageResponse,
  Group,
  Message,
  Session,
  TimetableSession,
  TimetableSessionRow,
} from 'timetable-model';
import * as React from 'react';
import { Button } from 'react-bootstrap';
import { OurMarkdown } from './OurMarkdown';
import styles from './SimpleProcessor.module.css';
import { SessionModal } from './SessionModal';

interface SessionCalendarEvent {
  id: number;
  title: string;
  start: Date;
  end: Date;
  colorCode: string;
}

const DragAndDropCalendar = withDragAndDrop<SessionCalendarEvent>(Calendar);

interface SimpleProcessorProps {
  timetableId: number;
}

export const SimpleProcessor = (props: SimpleProcessorProps): React.JSX.Element => {
  const [messages, setMessages] = React.useState<Message[]>([]);
  const [groups, setGroups] = React.useState<Group[]>([]);
  const [visibleGroups, setVisibleGroups] = React.useState<number[]>([]);
  const [sessions, setSessions] = React.useState<Session[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [showSessionModal, setShowSessionModal] = React.useState<boolean>(false);
  const [selectedSession, setSelectedSession] = React.useState<TimetableSessionRow | null>(null);
  const ref = React.useRef<HTMLTextAreaElement>(null);

  React.useEffect(() => {
    // Initialize the conversation with a system prompt
    const initializeConversation = async () => {
      setLoading(true);
      try {
        const response: ChatInitializationResponse = await fetchApi(
          'GET',
          `/main/ai-timetable/initialize/${props.timetableId}`,
        );
        setGroups(response.groups);
        setVisibleGroups(response.groups.map((g) => g.id));
        setSessions(response.sessions);
        setMessages(response.messages);
        setLoading(false);
      } catch (error) {
        notifyError(error, 'An error occurred while initializing the conversation');
        setLoading(false);
      }
    };
    void initializeConversation();
  }, []); // timetableId is not expected to change, so we can leave it out of the dependency array

  const submitPrompt = async () => {
    if (loading) {
      return;
    }
    const message = ref.current?.value;
    if (!message) {
      return;
    }
    setLoading(true);
    try {
      const response: ChatMessageResponse = await fetchApi('POST', '/main/ai-timetable/simple', {
        message,
        timetable_id: props.timetableId,
      } as ChatMessageRequest);
      setLoading(false);
      notifyResponse(response);
      setMessages((prev) => [...prev, { role: 'user', content: message }, response.message]);
      setGroups(response.groups);
      setSessions(response.sessions);
      setVisibleGroups((prev) =>
        prev.length === 0
          ? response.groups.map((g) => g.id)
          : prev.filter((g) => response.groups.map((g) => g.id).includes(g)),
      );
      if (ref.current) {
        ref.current.value = '';
      }
    } catch (error) {
      notifyError(error, 'An error occurred while submitting the prompt');
      setLoading(false);
    }
  };

  const onPromptKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const isComposing = ((e.nativeEvent as unknown) as { isComposing?: boolean }).isComposing;
    if (e.key === 'Enter' && !e.shiftKey && !isComposing) {
      e.preventDefault();
      void submitPrompt();
    }
  };

  const toggleGroupVisibility = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, groupId: number) => {
    if (visibleGroups.includes(groupId)) {
      // if clicked group is currently visible:
      if (visibleGroups.length === groups.length) {
        // if all groups currently visible hide all except this one
        setVisibleGroups([groupId]);
      } else if (visibleGroups.length === 1) {
        // if only this group currently visible show all groups
        setVisibleGroups(groups.map((g) => g.id));
      } else {
        // else hide this one
        setVisibleGroups(visibleGroups.filter((g) => g !== groupId));
      }
    } else {
      // else show this one
      setVisibleGroups([...visibleGroups, groupId]);
    }
    e.preventDefault();
    e.stopPropagation();
  };

  const displayArgs = (functName: string) => (args: any[]) => {
    console.log(`Function ${functName} called with arguments:`, args);
  };

  const getGroupFromId = (groupId: number) => groups.find((g) => g.id === groupId);

  const createSession = (slotInfo: { start: Date; end: Date }) => {
    const start = new Date(slotInfo.start);
    if (start.getHours() === 0 && start.getMinutes() === 0) {
      start.setHours(9); // default to 9am
    }
    const end = new Date(slotInfo.end);
    if (end.getHours() === 0 && end.getMinutes() === 0) {
      end.setDate(start.getDate()); // default to same day as start
      end.setHours(10); // default to 10am
    }
    setSelectedSession({
      id: 0,
      title: null,
      timetable_id: props.timetableId,
      start_time: start.toISOString(),
      end_time: end.toISOString(),
      group_id: null,
      color_code: null,
      created_at: null,
      updated_at: null,
    });
    setShowSessionModal(true);
  };

  const moveSession = ({
    event: { id },
    start,
    end,
  }: {
    event: { id: number };
    start: Date | string;
    end: Date | string;
  }) => {
    const nextStart = new Date(start);
    const nextEnd = new Date(end);
    const newSessions = sessions.slice();
    const changedSessionIndex = newSessions.findIndex((s) => s.id === id);
    if (changedSessionIndex !== -1) {
      newSessions[changedSessionIndex] = {
        ...newSessions[changedSessionIndex],
        startTime: nextStart.toISOString(),
        endTime: nextEnd.toISOString(),
      };
      setSessions(newSessions);

      const payload = makeCompoundPayload('save');
      addRowToPayload(
        payload,
        TimetableSession,
        convertToFormRowUpdate(TimetableSession, {
          id,
          start_time: nextStart.toISOString(),
          end_time: nextEnd.toISOString(),
        }),
      );

      // no need to wait!
      fetchApi('POST', '/main/~/timetable-move-session', payload);
    }
  };

  const updateSession = (eventInfo: { id: number }) => {
    const session = sessions.find((s) => s.id === eventInfo.id);
    console.log('Session selected:', eventInfo, session);
    setSelectedSession({
      id: session.id,
      title: session.title,
      timetable_id: props.timetableId,
      start_time: session.startTime,
      end_time: session.endTime,
      group_id: session.groupId,
      color_code: session.colorCode,
      created_at: null,
      updated_at: null,
    });
    setShowSessionModal(true);
  };

  const events: SessionCalendarEvent[] = sessions
    .filter((session) => visibleGroups.includes(session.groupId))
    .sort((a, b) => getGroupFromId(a.groupId)?.name.localeCompare(getGroupFromId(b.groupId)?.name || '') || 0)
    .map((session) => ({
      id: session.id,
      title: `${session.title} / ${groups.find((g) => g.id === session.groupId)?.name || 'Unknown'}`,
      start: new Date(session.startTime),
      end: new Date(session.endTime),
      colorCode: session.colorCode || groups.find((g) => g.id === session.groupId)?.colorCode || '#3174ad',
    }));

  return (
    <div className={styles.Outer}>
      <PageTitle title="Amend this Timetable" />
      <div className={styles.Inner}>
        <div className={styles.ChatPane}>
          <p>Ask our AI assistant to create, update or delete groups and sessions:</p>
          <ul>
            {messages.map((msg, idx) => (
              <li key={idx}>
                {msg.role === 'user' ? <b>User:</b> : <b>Assistant:</b>}
                <OurMarkdown content={msg.content} />
              </li>
            ))}
          </ul>
          <textarea
            id="prompt"
            ref={ref}
            placeholder="Enter your prompt"
            rows={5}
            onKeyDown={onPromptKeyDown}
            disabled={loading}
          />
          <div>
            <Button onClick={submitPrompt} disabled={loading} variant="primary">
              {loading ? 'Waiting...' : 'Send'}
            </Button>
          </div>
        </div>
        <div className={styles.CalendarPane}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            <b>Groups:</b>
            {groups.map((group, idx) => (
              <Button
                key={idx}
                style={{
                  borderColor: group.colorCode,
                  borderWidth: 3,
                }}
                type="button"
                size="sm"
                onClick={(e) => toggleGroupVisibility(e, group.id)}
                variant={visibleGroups.includes(group.id) ? 'secondary' : 'outline-secondary'}
              >
                {group.name}
              </Button>
            ))}
            {groups.length === 0 && <span style={{ marginLeft: '1rem' }}>No groups created yet.</span>}
          </div>
          <br />
          <DragAndDropCalendar
            localizer={calendarLocalizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            defaultView="month"
            views={['month', 'week', 'day', 'agenda']}
            formats={{
              timeGutterFormat: 'HH:mm',
              eventTimeRangeFormat: () => '',
            }}
            selectable="ignoreEvents"
            onEventDrop={moveSession}
            onEventResize={moveSession}
            onSelectEvent={updateSession}
            onSelectSlot={createSession}
            eventPropGetter={(event) => {
              return {
                style: {
                  backgroundColor: event.colorCode,
                  borderColor: event.colorCode,
                },
              };
            }}
          />
        </div>
      </div>
      {showSessionModal && (
        <SessionModal
          onSave={(updatedSession: TimetableSessionRow) => {
            setShowSessionModal(false);
            const newSessions = sessions.slice();
            const changedSessionIndex = newSessions.findIndex((s) => s.id === updatedSession.id);
            console.log('Session updated:', updatedSession, changedSessionIndex);
            if (changedSessionIndex !== -1) {
              newSessions.splice(changedSessionIndex, 1);
            }
            newSessions.push({
              id: updatedSession.id,
              title: updatedSession.title,
              startTime: updatedSession.start_time,
              endTime: updatedSession.end_time,
              groupId: updatedSession.group_id,
              colorCode: updatedSession.color_code,
            });
            setSessions(newSessions);
          }}
          onCancel={() => setShowSessionModal(false)}
          isCreate={!selectedSession?.id}
          session={selectedSession}
          groups={groups}
        />
      )}
    </div>
  );
};
